import '@theme/ncc-lightbox';

/**
 * NCC: lightbox for stock image blocks (blocks/image.liquid, setting "Open in a lightbox on click").
 * Each such block is a link to its full-size image carrying `data-ncc-lightbox="<section id>"`. A click on one
 * builds (once per section, rebuilt when its images change) the same <ncc-lightbox> the product gallery renders
 * in Liquid, from every lightbox image of that section in document order, and opens it on the clicked one.
 * Without JavaScript the link simply opens the picture.
 */

const ICON_CLOSE =
  '<svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.1765 12.025C11.4108 12.2594 11.7907 12.2594 12.025 12.025C12.2594 11.7907 12.2594 11.4108 12.025 11.1765L8.84931 8.00078L12.025 4.82505C12.2594 4.59073 12.2594 4.21083 12.025 3.97652C11.7907 3.7422 11.4108 3.7422 11.1765 3.97652L8.00078 7.15225L4.82505 3.97652C4.59073 3.7422 4.21083 3.7422 3.97652 3.97652C3.7422 4.21083 3.7422 4.59073 3.97652 4.82505L7.15225 8.00078L3.97652 11.1765C3.7422 11.4108 3.7422 11.7907 3.97652 12.025C4.21083 12.2594 4.59073 12.2594 4.82505 12.025L8.00078 8.84931L11.1765 12.025Z" fill="currentColor"/></svg>';
const ICON_ARROW =
  '<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"><path d="M4 12h16M14 6l6 6-6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const WIDTHS = [800, 1200, 1600, 2000, 2600];

/** @param {string} value */
const escapeHtml = (value) =>
  value.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c);

/**
 * Re-sizes a Shopify CDN image URL (the block links to the 2600px rendition).
 * @param {string} url
 * @param {number} width
 */
const sized = (url, width) =>
  /[?&]width=\d+/.test(url) ? url.replace(/([?&])width=\d+/, `$1width=${width}`) : `${url}${url.includes('?') ? '&' : '?'}width=${width}`;

/** @param {HTMLAnchorElement} link */
const altOf = (link) => link.querySelector('img')?.getAttribute('alt') ?? '';

/**
 * @param {string} group
 * @param {HTMLAnchorElement[]} links
 */
function build(group, links) {
  // Theme is a script-level const in snippets/scripts.liquid (a global binding, not a window property)
  const t = typeof Theme !== 'undefined' ? /** @type {any} */ (Theme).translations ?? {} : {};
  const many = links.length > 1;
  const items = links
    .map((link, i) => {
      const src = link.href;
      const srcset = WIDTHS.map((w) => `${sized(src, w)} ${w}w`).join(', ');
      return `<figure class="ncc-lightbox__item${i === 0 ? ' ncc-lightbox__item--active' : ''}" ref="media[]" aria-hidden="${i === 0 ? 'false' : 'true'}">
        <img class="ncc-lightbox__image" src="${escapeHtml(sized(src, 1600))}" srcset="${escapeHtml(srcset)}" sizes="(min-width: 750px) min(92vw, 1400px), 100vw" loading="lazy" alt="${escapeHtml(altOf(link))}">
      </figure>`;
    })
    .join('');
  const thumbs = links
    .map(
      (link, i) => `<button type="button" class="ncc-lightbox__thumb" aria-label="${i + 1} / ${links.length}" aria-current="${i === 0 ? 'true' : 'false'}" on:click="/handleThumbnailClick/${i}">
        <img src="${escapeHtml(sized(link.href, 240))}" loading="lazy" alt="${escapeHtml(altOf(link))}">
      </button>`
    )
    .join('');
  const arrow = (/** @type {string} */ dir, /** @type {string} */ label) =>
    many
      ? `<button type="button" class="ncc-lightbox__arrow ncc-lightbox__arrow--${dir}" ref="${dir}" aria-label="${escapeHtml(label)}" on:click="/${dir}">${ICON_ARROW}</button>`
      : '';

  const lightbox = document.createElement('ncc-lightbox');
  lightbox.className = 'ncc-lightbox ncc-lightbox--images';
  lightbox.dataset.group = group;
  lightbox.dataset.key = links.map((link) => link.href).join('|');
  lightbox.innerHTML = `<dialog class="ncc-lightbox__dialog" ref="dialog" on:keydown="/handleKeyDown" aria-label="${escapeHtml(t.image_gallery ?? 'Image gallery')}" tabindex="-1">
    <div class="ncc-lightbox__panel">
      <button type="button" class="ncc-lightbox__close" aria-label="${escapeHtml(t.close ?? 'Close')}" on:click="/close">${ICON_CLOSE}</button>
      <div class="ncc-lightbox__stage" ref="stage">
        ${arrow('previous', t.previous ?? 'Previous')}
        <div class="ncc-lightbox__frame">${items}</div>
        ${arrow('next', t.next ?? 'Next')}
      </div>
      <div class="ncc-lightbox__footer">
        <span class="ncc-lightbox__counter" ref="counter">1 / ${links.length}</span>
        <div class="ncc-lightbox__thumbs" ref="thumbnails"${many ? '' : ' hidden'}>${thumbs}</div>
      </div>
    </div>
  </dialog>`;
  document.body.append(lightbox);
  return lightbox;
}

/**
 * @param {string} group
 * @param {HTMLAnchorElement[]} links
 */
function ensure(group, links) {
  const key = links.map((link) => link.href).join('|');
  const existing = /** @type {HTMLElement | null} */ (document.querySelector(`ncc-lightbox[data-group="${CSS.escape(group)}"]`));
  if (existing && existing.dataset.key === key) return existing;
  existing?.remove();
  return build(group, links);
}

document.addEventListener('click', (event) => {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const target = event.target;
  if (!(target instanceof Element)) return;
  const link = /** @type {HTMLAnchorElement | null} */ (target.closest('a[data-ncc-lightbox]'));
  if (!link) return;
  const group = link.dataset.nccLightbox ?? '';
  const links = /** @type {HTMLAnchorElement[]} */ (
    Array.from(document.querySelectorAll(`a[data-ncc-lightbox="${CSS.escape(group)}"]`))
  );
  const index = links.indexOf(link);
  if (index < 0) return;
  event.preventDefault();
  const lightbox = /** @type {any} */ (ensure(group, links));
  lightbox.open(index);
});
